import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  computed,
  effect,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import * as pbi from 'powerbi-client';
import { filter } from 'rxjs';

import { PowerBiService } from '@app/power-bi/services/power-bi.service';
import { ACCESS_STORE } from '@core-interactions/stores/access.store';
import { ExportPowerBiReportDialogComponent } from '@power-bi/components/export-power-bi-report-dialog/export-power-bi-report-dialog.component';
import { PowerBiEmbedComponent } from '@power-bi/components/power-bi-embed/power-bi-embed.component';
import { ExportPowerBIReportFileFormat } from '@power-bi/models/export-power-bi-report-file-format';
import { ExportPowerBIReportOptions } from '@power-bi/models/export-power-bi-report-options';
import { SeoService } from '@shared/services/seo.service';
import { ToastNotificationsService } from '@shared/services/toast-notifications.service';
import { APP_STORE } from '@stores/app.store';

@Component({
  selector: 'app-power-bi-embed-container',
  templateUrl: './power-bi-embed-container.component.html',
  styleUrls: ['./power-bi-embed-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    PowerBiEmbedComponent
  ]
})
export class PowerBiEmbedContainerComponent {
  readonly #dialog = inject(MatDialog);
  readonly #appStore = inject(APP_STORE);
  readonly #accessStore = inject(ACCESS_STORE);
  readonly #powerBiService = inject(PowerBiService);
  readonly #seoService = inject(SeoService);
  readonly #toastNotificationsService = inject(ToastNotificationsService);
  readonly #cdr = inject(ChangeDetectorRef);
  readonly #destroyRef = inject(DestroyRef);

  readonly selectedReport = computed(() => {
    const reportId = this.#appStore.selectedReportId();
    const reports = this.#accessStore.reports().entities;
    const selectedReport = reports.find(x => x.id === reportId);

    return selectedReport ?? null;
  });

  constructor() {
    effect(() => {
      const report = this.selectedReport();

      if (report) {
        this.#seoService.updateTitle(report.customName);
      }
    });
  }

  pbiEmbedReport: pbi.Report | null = null;
  loaded = false;

  async onExportReport(): Promise<void> {
    if (this.pbiEmbedReport) {
      const bookmarksManager = this.pbiEmbedReport.bookmarksManager;

      const captureBookmarkOptions: pbi.models.ICaptureBookmarkOptions = {
        allPages: true,
        personalizeVisuals: true
      };

      const bookmark: pbi.models.IReportBookmark =
        await bookmarksManager.capture(captureBookmarkOptions);

      const dialogRef = this.#dialog.open(ExportPowerBiReportDialogComponent, {
        disableClose: false,
        autoFocus: false
      });

      dialogRef
        .afterClosed()
        .pipe(
          takeUntilDestroyed(this.#destroyRef),
          filter(x => x != null)
        )
        .subscribe((format: ExportPowerBIReportFileFormat) => {
          const exportOptions: ExportPowerBIReportOptions = {
            format,
            state: bookmark.state ?? ''
          };

          this.#toastNotificationsService.info(
            'Your report is being exported and will begin downloading when ready'
          );

          const report = this.selectedReport();
          if (!report) {
            this.#toastNotificationsService.error('Exporting report failed');
            return;
          }

          this.#powerBiService.exportReport(report, exportOptions);
        });
    }
  }

  onEmbeddedReportChange(pbiEmbedReport: pbi.Embed): void {
    this.pbiEmbedReport = pbiEmbedReport as pbi.Report;

    this.pbiEmbedReport.on('loaded', () => {
      this.loaded = true;
      this.#cdr.detectChanges();
    });
  }
}
